import { api } from '../api';
import { isAdmin, navigate, state } from '../app';
import { esc, $, $$, setHTML, formatCurrency, formatDate, statusBadge, icons } from '../helpers';
import { toast, openModal, closeModal, setBreadcrumb, skeletonTable } from '../ui';

declare const jspdf: any;

export async function renderCompliance() {
  if (!isAdmin()) return navigate('/dashboard');
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'IRS Compliance' }]);

  // SAFE: all static HTML
  setHTML(content, '<div class="page-enter"><div class="page-header"><h1>IRS Compliance</h1></div>'
    + '<div class="settings-tabs">'
    + '<button class="settings-tab active" data-tab="form8300">Form 8300</button>'
    + '<button class="settings-tab" data-tab="fmv">FMV Records</button>'
    + '</div>'
    + '<div id="compliance-content">'
    + '<div class="settings-section" id="tab-form8300">' + skeletonTable(6, 4) + '</div>'
    + '<div class="settings-section" id="tab-fmv" style="display:none">' + skeletonTable(7, 4) + '</div>'
    + '</div></div>');

  icons();

  // Tab switching
  $$('.settings-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      $$('.settings-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      $$('.settings-section').forEach((s: any) => s.style.display = 'none');
      const tgt = document.getElementById('tab-' + tab.getAttribute('data-tab'));
      if (tgt) (tgt as HTMLElement).style.display = 'block';
    });
  });

  // Load both tabs
  loadForm8300();
  loadFMV();
}

async function loadForm8300() {
  try {
    const data = await api.get('/compliance/form8300');
    const filings = data.filings || [];
    let rows = '';
    // SAFE: all dynamic content escaped via esc()/formatCurrency()/formatDate()
    filings.forEach((f: any) => {
      const statusClass = f.is_overdue ? 'danger' : (f.status === 'filed' ? 'success' : 'warning');
      const statusLabel = f.is_overdue ? 'OVERDUE' : f.status.charAt(0).toUpperCase() + f.status.slice(1);
      const badge = '<span style="background:var(--' + statusClass + '-soft);color:var(--' + statusClass + ');padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">' + esc(statusLabel) + '</span>';
      const pdfBtn = '<button class="btn btn-ghost btn-sm" data-pdf8300="' + f.id + '" title="Generate Form 8300 PDF"><i data-lucide="file-text"></i></button>';
      rows += '<tr><td>' + esc(f.client_name) + '</td><td>' + esc(f.client_company || '\u2014') + '</td><td><strong>' + formatCurrency(f.trigger_amount_usd) + '</strong></td><td>' + formatDate(f.trigger_date) + '</td><td>' + formatDate(f.filing_deadline) + '</td><td>' + badge + '</td><td>'
        + (f.status === 'pending' ? '<button class="btn btn-primary btn-sm" data-file8300="' + f.id + '">Mark Filed</button> ' : (f.irs_confirmation ? '<span class="font-mono" style="font-size:0.78rem">' + esc(f.irs_confirmation) + '</span> ' : esc(f.filed_at ? 'Filed ' + formatDate(f.filed_at) : '') + ' '))
        + pdfBtn
        + '</td></tr>';
    });
    const tableHtml = !filings.length
      ? '<div class="empty-state"><p>No Form 8300 filings required</p></div>'
      : '<div class="table-wrapper"><table><thead><tr><th>Client</th><th>Company</th><th>Amount</th><th>Trigger Date</th><th>Deadline</th><th>Status</th><th>Actions</th></tr></thead><tbody>' + rows + '</tbody></table></div>';

    // SAFE: tableHtml built from escaped content
    setHTML(document.getElementById('tab-form8300')!, '<div class="card"><div class="card-header"><h3>Form 8300 — Digital Asset Cash Receipts</h3></div><div class="card-body no-pad">' + tableHtml + '</div></div>'
      + '<p style="margin-top:0.8rem;font-size:0.8rem;color:var(--text-muted)">IRS requires Form 8300 within 15 days of receiving $10,000+ in digital assets in a single transaction or related transactions within 24 hours.</p>');

    icons();

    // Mark as filed handlers
    $$('[data-file8300]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-file8300')!;
        openModal('Mark Form 8300 as Filed', '<form id="file8300-form">'
          + '<div class="form-group"><label>IRS Confirmation Number</label><input type="text" name="irs_confirmation" placeholder="e.g. 8300-2026-XXXXX"></div>'
          + '<div class="form-group"><label>Notes</label><textarea name="notes" rows="2" placeholder="Optional notes"></textarea></div>'
          + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="file8300-cancel">Cancel</button><button type="submit" class="btn btn-primary">Confirm Filed</button></div></form>');
        document.getElementById('file8300-cancel')?.addEventListener('click', closeModal);
        document.getElementById('file8300-form')?.addEventListener('submit', async (e) => {
          e.preventDefault();
          const fd = new FormData(e.target as HTMLFormElement);
          try {
            await api.put('/compliance/form8300/' + id, {
              status: 'filed',
              irs_confirmation: fd.get('irs_confirmation') || '',
              notes: fd.get('notes') || '',
            });
            toast('Form 8300 marked as filed', 'success');
            closeModal();
            loadForm8300();
          } catch (err: any) { toast(err.message, 'error'); }
        });
      });
    });

    // PDF generation handlers
    $$('[data-pdf8300]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-pdf8300')!;
        const filing = filings.find((f: any) => String(f.id) === id);
        if (filing) generateForm8300PDF(filing);
      });
    });
  } catch (err: any) { toast(err.message, 'error'); }
}

// ─── Form 8300 PDF Generator ────────────────────────────────────────────────

const COMPANY_DEFAULTS = {
  name: 'Gravant Labs LLC',
  address: '1201 Orange Street, Suite 600',
  cityStateZip: 'Wilmington, DE 19801',
  ein: '35-2952235',
  email: 'billing@gravantlabs.com',
  phone: '',
  nature: 'Software Development & Consulting',
};

function generateForm8300PDF(filing: any) {
  const doc = new jspdf.jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 18;
  const marginR = pageW - 18;
  const contentW = marginR - marginL;

  const admin = state.user;
  const company = {
    ...COMPANY_DEFAULTS,
    phone: admin?.phone || COMPANY_DEFAULTS.phone,
    email: admin?.email || COMPANY_DEFAULTS.email,
  };

  // Parse client name into first/last
  const clientNameParts = (filing.client_name || '').trim().split(/\s+/);
  const clientFirst = clientNameParts.slice(0, -1).join(' ') || clientNameParts[0] || '';
  const clientLast = clientNameParts.length > 1 ? clientNameParts[clientNameParts.length - 1] : '';

  // Parse payment IDs
  let paymentIds: number[] = [];
  try { paymentIds = JSON.parse(filing.payment_ids || '[]'); } catch (_e) { /* ignore */ }

  // ──────────────────── HELPER FUNCTIONS ────────────────────

  function sectionHeader(y: number, text: string): number {
    doc.setFillColor(230, 230, 230);
    doc.rect(marginL, y - 4.5, contentW, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(30, 30, 30);
    doc.text(text, marginL + 2, y);
    return y + 8;
  }

  function labelField(x: number, y: number, label: string, value: string, width: number): void {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 100, 100);
    doc.text(label, x, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 30, 30);
    doc.text(value || '', x, y + 4.5);
    // Underline
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(x, y + 6, x + width, y + 6);
  }

  function checkbox(x: number, y: number, checked: boolean, label: string): void {
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.3);
    doc.rect(x, y - 3, 3.5, 3.5);
    if (checked) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(30, 30, 30);
      doc.text('X', x + 0.5, y);
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(50, 50, 50);
    doc.text(label, x + 5, y);
  }

  // ──────────────────── PAGE 1 ────────────────────

  let y = 15;

  // DRAFT watermark
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(70);
  doc.setFont('helvetica', 'bold');
  doc.setGState(new doc.GState({ opacity: 0.12 }));
  doc.text('DRAFT', pageW / 2, pageH / 2, { align: 'center', angle: 35 });
  doc.setGState(new doc.GState({ opacity: 1 }));

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(50, 50, 50);
  doc.text('Department of the Treasury — Internal Revenue Service / Financial Crimes Enforcement Network', marginL, y);
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 30);
  y += 8;
  doc.text('Form 8300', marginL, y);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Report of Cash Payments Over $10,000', marginL + 30, y);

  // OMB number (right side)
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text('OMB No. 1506-0018', marginR, y - 7, { align: 'right' });
  doc.setFontSize(6.5);
  doc.text('For calendar year: ' + new Date(filing.trigger_date).getFullYear(), marginR, y - 3, { align: 'right' });

  y += 5;
  doc.setDrawColor(30, 30, 30);
  doc.setLineWidth(0.6);
  doc.line(marginL, y, marginR, y);
  y += 3;

  // Draft notice
  doc.setFillColor(255, 248, 220);
  doc.rect(marginL, y, contentW, 10, 'F');
  doc.setDrawColor(200, 180, 80);
  doc.setLineWidth(0.3);
  doc.rect(marginL, y, contentW, 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(140, 100, 0);
  doc.text('DRAFT \u2014 For review purposes only. File electronically at BSA E-Filing System (bsaefiling.fincen.treas.gov)', marginL + 3, y + 6);
  y += 15;

  // ─── Part I ───
  y = sectionHeader(y, 'Part I \u2014 Identity of Individual From Whom the Cash Was Received');
  y += 2;

  // Row 1: Last name, First name, M.I.
  labelField(marginL, y, '2. Last name', clientLast, 55);
  labelField(marginL + 60, y, '3. First name', clientFirst, 50);
  labelField(marginL + 115, y, 'M.I.', '', 15);
  y += 12;

  // Row 2: TIN
  const clientTin = filing.client_tax_id || filing.client_vat || '';
  labelField(marginL, y, '4. Taxpayer identification number (TIN)', clientTin, 55);
  labelField(marginL + 60, y, '5. Date of birth (MM/DD/YYYY)', '', 50);
  y += 12;

  // Row 3: Address
  labelField(marginL, y, '6. Address (number, street, and apt. or suite no.)', filing.client_address || '', contentW);
  y += 12;

  // Row 4: City, State, ZIP, Country
  const halfW = contentW / 2 - 5;
  labelField(marginL, y, '7. City', '', halfW * 0.5);
  labelField(marginL + halfW * 0.55, y, '8. State', '', halfW * 0.25);
  labelField(marginL + halfW * 0.85, y, '9. ZIP code', '', halfW * 0.3);
  labelField(marginL + halfW + 10, y, '10. Country (if not U.S.)', filing.client_country || '', halfW - 5);
  y += 12;

  // Row 5: Occupation, ID fields
  labelField(marginL, y, '11. Occupation, profession, or business', '', halfW);
  labelField(marginL + halfW + 10, y, '12. Identifying document (ID)', '', halfW - 5);
  y += 12;

  // Row 6: Issued by, Number
  labelField(marginL, y, '13. Issued by', '', halfW);
  labelField(marginL + halfW + 10, y, '14. Number', '', halfW - 5);
  y += 14;

  // ─── Part II ───
  y = sectionHeader(y, 'Part II \u2014 Person on Whose Behalf This Transaction Was Conducted');
  y += 2;

  const hasBusiness = filing.client_type === 'business' && filing.client_company;

  if (!hasBusiness) {
    checkbox(marginL, y + 1, true, '15. If the individual named above is the same as the person on whose behalf the transaction was conducted, check here and go to Part III.');
    y += 10;
  } else {
    checkbox(marginL, y + 1, false, '15. Same as Part I (not applicable \u2014 business entity below)');
    y += 8;
    labelField(marginL, y, '16. Individual\'s last name or Organization\'s name', filing.client_company, contentW);
    y += 12;
    const bizTin = filing.client_tax_id || filing.client_vat || '';
    labelField(marginL, y, '17. Taxpayer identification number (EIN)', bizTin, 55);
    labelField(marginL + 60, y, '18. Doing business as (DBA) name', '', contentW - 60);
    y += 12;
    labelField(marginL, y, '19. Address', filing.client_address || '', contentW);
    y += 12;
    labelField(marginL, y, '20. City', '', halfW * 0.5);
    labelField(marginL + halfW * 0.55, y, '21. State', '', halfW * 0.25);
    labelField(marginL + halfW * 0.85, y, '22. ZIP code', '', halfW * 0.3);
    labelField(marginL + halfW + 10, y, '23. Country (if not U.S.)', filing.client_country || '', halfW - 5);
    y += 12;
  }

  y += 2;

  // ─── Part III ───
  y = sectionHeader(y, 'Part III \u2014 Description of Transaction and Method of Payment');
  y += 2;

  // Date and amount
  const triggerDate = filing.trigger_date ? formatDate(filing.trigger_date) : '';
  const triggerAmt = filing.trigger_amount_usd ? formatCurrency(filing.trigger_amount_usd) : '';
  labelField(marginL, y, '24. Date cash received (MM/DD/YYYY)', triggerDate, 55);
  labelField(marginL + 60, y, '25. Total cash received', triggerAmt, 55);
  labelField(marginL + 120, y, '26. If cashier\'s check(s), amount', '', contentW - 120);
  y += 14;

  // Nature of transaction checkboxes
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text('27-33. Nature of transaction:', marginL, y);
  y += 5;
  checkbox(marginL, y, false, 'a. Personal property purchased');
  checkbox(marginL + 55, y, false, 'b. Real property purchased');
  checkbox(marginL + 110, y, false, 'c. Personal services provided');
  y += 6;
  checkbox(marginL, y, true, 'd. Business services provided');
  checkbox(marginL + 55, y, false, 'e. Intangible property purchased');
  checkbox(marginL + 110, y, false, 'f. Debt obligations paid');
  y += 6;
  checkbox(marginL, y, false, 'g. Exchange of cash');
  checkbox(marginL + 55, y, false, 'h. Escrow or trust funds');
  checkbox(marginL + 110, y, false, 'z. Other (specify)');
  y += 10;

  // Description
  const cryptoCurrency = 'USDT'; // Default; could be enhanced if stored in filing
  labelField(marginL, y, '34. Specific description of transaction', 'Software consulting services. Payment received in ' + cryptoCurrency + ' (stablecoin) via blockchain.', contentW);
  y += 14;

  // IRS notice
  doc.setFillColor(245, 245, 255);
  doc.rect(marginL, y, contentW, 8, 'F');
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6.5);
  doc.setTextColor(80, 80, 130);
  doc.text('Note: Per IRS Announcement 2024-04, digital asset Form 8300 reporting is deferred pending final regulations.', marginL + 2, y + 5);
  y += 14;

  // ─── Part IV ───
  y = sectionHeader(y, 'Part IV \u2014 Business That Received Cash');
  y += 2;

  labelField(marginL, y, '35. Name of business that received cash', company.name, contentW);
  y += 12;
  labelField(marginL, y, '36. Employer identification number (EIN)', company.ein, 55);
  labelField(marginL + 60, y, '37. Nature of your business', company.nature, contentW - 60);
  y += 12;
  labelField(marginL, y, '38. Address (number, street, and apt. or suite no.)', company.address, contentW);
  y += 12;
  labelField(marginL, y, '39. City', 'Wilmington', halfW * 0.5);
  labelField(marginL + halfW * 0.55, y, '40. State', 'DE', halfW * 0.25);
  labelField(marginL + halfW * 0.85, y, '41. ZIP code', '19801', halfW * 0.3);
  labelField(marginL + halfW + 10, y, '42. Country', 'United States', halfW - 5);
  y += 14;

  // Signature area
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(marginL, y, marginR, y);
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(50, 50, 50);
  doc.text('Sign Here', marginL, y);
  y += 6;
  labelField(marginL, y, '43. Signature', '', contentW * 0.55);
  labelField(marginL + contentW * 0.6, y, '44. Date signed', '', contentW * 0.35);
  y += 12;
  labelField(marginL, y, '45. Print name of contact', admin?.name || '', contentW * 0.55);
  labelField(marginL + contentW * 0.6, y, '46. Contact phone', company.phone || company.email, contentW * 0.35);

  // Footer line
  const footerY = pageH - 12;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(marginL, footerY - 3, marginR, footerY - 3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(150, 150, 150);
  doc.text('Form 8300 (Rev. 12-2023)  |  ' + company.name + '  |  Generated ' + new Date().toLocaleDateString(), pageW / 2, footerY, { align: 'center' });
  doc.text('Page 1 of 2', marginR, footerY, { align: 'right' });

  // ──────────────────── PAGE 2 ────────────────────

  doc.addPage();
  y = 15;

  // DRAFT watermark on page 2
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(70);
  doc.setFont('helvetica', 'bold');
  doc.setGState(new doc.GState({ opacity: 0.12 }));
  doc.text('DRAFT', pageW / 2, pageH / 2, { align: 'center', angle: 35 });
  doc.setGState(new doc.GState({ opacity: 1 }));

  // Page 2 header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  doc.text('Form 8300 \u2014 Page 2', marginL, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text('OMB No. 1506-0018', marginR, y, { align: 'right' });
  y += 4;
  doc.setDrawColor(30, 30, 30);
  doc.setLineWidth(0.6);
  doc.line(marginL, y, marginR, y);
  y += 8;

  // ─── Comments Section ───
  y = sectionHeader(y, 'Comments / Additional Information');
  y += 4;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(50, 50, 50);

  const comments: string[] = [];
  comments.push('Filing ID: #' + filing.id);
  comments.push('Client: ' + (filing.client_name || 'N/A') + (filing.client_company ? ' (' + filing.client_company + ')' : ''));
  comments.push('Client Email: ' + (filing.client_email || 'N/A'));
  comments.push('Client Phone: ' + (filing.client_phone || 'N/A'));
  comments.push('');
  comments.push('Transaction Details:');
  comments.push('  Trigger Amount (USD): ' + formatCurrency(filing.trigger_amount_usd));
  comments.push('  Trigger Date: ' + (filing.trigger_date ? formatDate(filing.trigger_date) : 'N/A'));
  comments.push('  Filing Deadline: ' + (filing.filing_deadline ? formatDate(filing.filing_deadline) : 'N/A'));
  comments.push('  Status: ' + (filing.status || 'pending').toUpperCase() + (filing.is_overdue ? ' (OVERDUE)' : ''));
  if (filing.irs_confirmation) comments.push('  IRS Confirmation: ' + filing.irs_confirmation);
  if (filing.filed_at) comments.push('  Filed At: ' + formatDate(filing.filed_at));
  comments.push('');
  comments.push('Payment IDs Referenced: ' + (paymentIds.length ? paymentIds.join(', ') : 'N/A'));
  comments.push('');
  comments.push('Payment was received in digital assets (stablecoin) via blockchain transfer.');
  comments.push('Per IRS Announcement 2024-04, the requirement to report digital assets on');
  comments.push('Form 8300 is deferred pending the issuance of final regulations. This form');
  comments.push('is generated for internal compliance tracking and review purposes.');
  if (filing.notes) {
    comments.push('');
    comments.push('Notes: ' + filing.notes);
  }

  comments.forEach(line => {
    if (y > pageH - 30) { doc.addPage(); y = 20; }
    doc.text(line, marginL + 2, y);
    y += 5;
  });

  y += 8;

  // Business info summary box
  doc.setFillColor(248, 248, 245);
  doc.rect(marginL, y, contentW, 28, 'F');
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.rect(marginL, y, contentW, 28);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 100, 100);
  doc.text('BUSINESS THAT RECEIVED CASH', marginL + 3, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(50, 50, 50);
  doc.text(company.name + '  |  EIN: ' + company.ein, marginL + 3, y);
  y += 4.5;
  doc.text(company.address, marginL + 3, y);
  y += 4.5;
  doc.text(company.cityStateZip + '  |  ' + company.email, marginL + 3, y);

  y += 14;

  // Generated timestamp
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(marginL, y, marginR, y);
  y += 6;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text('This document was generated on ' + new Date().toLocaleString() + ' by ' + (admin?.name || 'System') + '.', marginL, y);
  y += 4;
  doc.text('It is a DRAFT for internal review. The official filing must be submitted via the BSA E-Filing System.', marginL, y);
  y += 4;
  doc.text('Generated from: ' + company.name + ' Customer Portal (customers.gravantlabs.com)', marginL, y);

  // Footer
  const footer2Y = pageH - 12;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(marginL, footer2Y - 3, marginR, footer2Y - 3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(150, 150, 150);
  doc.text('Form 8300 (Rev. 12-2023)  |  ' + company.name + '  |  Generated ' + new Date().toLocaleDateString(), pageW / 2, footer2Y, { align: 'center' });
  doc.text('Page 2 of 2', marginR, footer2Y, { align: 'right' });

  // Save
  const filename = 'Form8300_' + (filing.client_name || 'client').replace(/\s+/g, '_') + '_' + (filing.trigger_date || '').split('T')[0] + '.pdf';
  doc.save(filename);
}

async function loadFMV() {
  try {
    const year = new Date().getFullYear().toString();
    const data = await api.get('/compliance/fmv?year=' + year);
    const records = data.records || [];
    let rows = '';
    // SAFE: all dynamic content escaped
    records.forEach((r: any) => {
      rows += '<tr><td class="font-mono">' + esc(r.invoice_number) + '</td><td>' + esc(r.client_name) + '</td><td>' + esc(r.crypto_currency) + '</td><td>' + esc(r.amount_crypto) + '</td><td>$' + esc(Number(r.fmv_usd_per_unit).toFixed(4)) + '</td><td>' + formatCurrency(r.fmv_total_usd) + '</td><td>' + esc(r.source) + '</td><td>' + formatDate(r.fetched_at) + '</td></tr>';
    });
    const tableHtml = !records.length
      ? '<div class="empty-state"><p>No FMV records for ' + esc(year) + '</p></div>'
      : '<div class="table-wrapper"><table><thead><tr><th>Invoice</th><th>Client</th><th>Currency</th><th>Amount</th><th>FMV/Unit</th><th>FMV Total</th><th>Source</th><th>Date</th></tr></thead><tbody>' + rows + '</tbody></table></div>';

    setHTML(document.getElementById('tab-fmv')!, '<div class="card"><div class="card-header"><h3>Fair Market Value Records \u2014 ' + esc(year) + '</h3></div><div class="card-body no-pad">' + tableHtml + '</div></div>'
      + '<p style="margin-top:0.8rem;font-size:0.8rem;color:var(--text-muted)">IRS requires recording the USD fair market value of digital assets at the exact time of receipt (IRS Notice 2014-21).</p>');
  } catch (err: any) { toast(err.message, 'error'); }
}
